import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import OverviewPage from "@/pages/OverviewPage";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { DashboardProvider, useDashboard } from "@/contexts/DashboardContext";
import DashboardLayout from "@/components/layout/DashboardLayout";

// Import page components
import AccountsPage from "@/components/dashboard/AccountsPage";
import KeywordSearchPageV2 from "@/components/dashboard/KeywordSearchPageV2";
import CompetitorTrackingPage from "@/components/dashboard/CompetitorTrackingPage";
import FollowerGrowthPage from "@/components/dashboard/FollowerGrowthPage";
import ContentGrowthPage from "@/components/dashboard/ContentGrowthPage";
import PostsPageWithAnalysis from "@/components/dashboard/PostsPageWithAnalysis";
import CommentsAnalysisPage from "@/components/dashboard/CommentsAnalysisPage";
import CommentSearchPage from "@/components/dashboard/CommentSearchPage";
import CrossPlatformComparison from "@/components/dashboard/CrossPlatformComparison";
import AIInsightsPage from "@/components/dashboard/AIInsightsPage";
import SettingsPage from "@/components/dashboard/SettingsPage";
import GuidePage from "@/pages/GuidePage";
import SyncLogViewer from "@/components/dashboard/SyncLogViewer";
import CorrelationPage from "@/components/dashboard/CorrelationPage";
import ArtistGroupsPage from "@/components/dashboard/ArtistGroupsPage";
import SpilloverPage from "@/components/dashboard/SpilloverPage";
import ReportsPage from "@/pages/ReportsPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <LoginPage />;
  }
  
  return children;
};

// App Content
const AppContent = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/*" element={
          <ProtectedRoute>
            <DashboardProvider>
              <Routes>
                <Route element={<DashboardLayout />}>
                  <Route index element={<OverviewPage />} />
                  <Route path="accounts" element={<AccountsPageWrapper />} />
                  <Route path="keyword-search" element={<KeywordSearchWrapper />} />
                  <Route path="competitor-tracking" element={<CompetitorTrackingPage />} />
                  <Route path="follower-growth" element={<FollowerGrowthPage />} />
                  <Route path="content-growth" element={<ContentGrowthPage />} />
                  <Route path="posts" element={<PostsPageWrapper />} />
                  <Route path="comments" element={<CommentsPageWrapper />} />
                  <Route path="comment-search" element={<CommentSearchPage />} />
                  <Route path="comparison" element={<CrossPlatformComparison />} />
                  <Route path="sync-logs" element={<SyncLogWrapper />} />
                  <Route path="insights" element={<InsightsPageWrapper />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="correlation" element={<CorrelationWrapper />} />
                  <Route path="artist-groups" element={<Navigate to="/settings/artist-groups" replace />} />
                  <Route path="settings/artist-groups" element={<ArtistGroupsWrapper />} />
                  <Route path="spillover" element={<SpilloverWrapper />} />
                  <Route path="reports" element={<ReportsWrapper />} />
                  <Route path="guide" element={<GuidePage />} />
                </Route>
              </Routes>
            </DashboardProvider>
          </ProtectedRoute>
        } />
      </Routes>
    </BrowserRouter>
  );
};

// Wrapper components to pass context props
const AccountsPageWrapper = () => {
  const { lang, startDate, endDate } = useDashboard();
  return <AccountsPage lang={lang} startDate={startDate} endDate={endDate} />;
};

const KeywordSearchWrapper = () => {
  const { lang } = useDashboard();
  return <KeywordSearchPageV2 lang={lang} />;
};

const SyncLogWrapper = () => {
  const { lang } = useDashboard();
  return <SyncLogViewer lang={lang} />;
};

const PostsPageWrapper = () => {
  const { lang, startDate, endDate, selectedPlatforms, selectedAccounts } = useDashboard();
  return (
    <PostsPageWithAnalysis
      lang={lang}
      startDate={startDate}
      endDate={endDate}
      selectedPlatforms={selectedPlatforms}
      selectedAccounts={selectedAccounts}
    />
  );
};

const CommentsPageWrapper = () => {
  const { lang, accounts, startDate, endDate, selectedPlatforms, selectedAccounts } = useDashboard();
  return (
    <CommentsAnalysisPage
      lang={lang}
      accounts={accounts}
      startDate={startDate}
      endDate={endDate}
      selectedPlatforms={selectedPlatforms}
      selectedAccounts={selectedAccounts}
    />
  );
};

const InsightsPageWrapper = () => {
  const { lang } = useDashboard();
  return <AIInsightsPage lang={lang} />;
};

const CorrelationWrapper = () => {
  const { lang } = useDashboard();
  return <CorrelationPage lang={lang} />;
};

const ArtistGroupsWrapper = () => {
  const { lang } = useDashboard();
  return <ArtistGroupsPage lang={lang} />;
};

const SpilloverWrapper = () => {
  const { lang } = useDashboard();
  return <SpilloverPage lang={lang} />;
};

const ReportsWrapper = () => {
  const { lang } = useDashboard();
  return <ReportsPage lang={lang} />;
};

function App() {
  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-background">
        <AuthProvider>
          <AppContent />
        </AuthProvider>
        <Toaster richColors position="top-right" />
      </div>
    </ThemeProvider>
  );
}

export default App;
